<template>
  <BsDropdown button-class="btn-primary btn-sm" menu-class="columns-menu" right>
    <template #button>
      <span aria-hidden="true">&#9881;</span>
      {{ buttonText }}
    </template>
    <div class="columns-selector">
      <div class="selector-section">
        <div class="section-title">{{ t("subject") }}</div>
        <div class="checks-grid">
          <BsCheckbox
            v-for="column in subjectColumns"
            :id="checkId(column.key)"
            :key="column.key"
            :checked="is_visible(column.key)"
            :disabled="column.disabled"
            :label="column.label"
            @change="(value) => emit('change', [column.key], value)"
          ></BsCheckbox>
        </div>
      </div>
      <div class="selector-section">
        <div class="section-title">{{ t("totals") }}</div>
        <div class="checks-grid">
          <BsCheckbox
            v-for="column in totalColumns"
            :id="checkId(column.key)"
            :key="column.key"
            :checked="is_visible(column.key)"
            :label="column.label"
            @change="(value) => emit('change', [column.key], value)"
          ></BsCheckbox>
        </div>
      </div>
      <!-- Matriz: una fila por División de tiempo y una columna por apartado, para no
           listar 4 checkboxes por división -->
      <div v-if="time_allocations.length > 0" class="selector-section">
        <table class="allocations-matrix">
          <thead>
            <tr>
              <th class="text-left">{{ t("divisions") }}</th>
              <th v-for="group in groups" :key="group.key">
                {{ group.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="all-row">
              <td class="text-left">
                <BsCheckbox
                  :id="checkId('all')"
                  v-bind="stateOf(allKeys)"
                  :label="t('all')"
                  @change="(value) => emit('change', allKeys, value)"
                ></BsCheckbox>
              </td>
              <td v-for="group in groups" :key="group.key">
                <BsCheckbox
                  :id="checkId(`all-${group.key}`)"
                  v-bind="stateOf(group.keys)"
                  :label="`${t('all')} - ${group.label}`"
                  hide-label
                  class="matrix-check"
                  @change="(value) => emit('change', group.keys, value)"
                ></BsCheckbox>
              </td>
            </tr>
            <tr v-for="row in allocationRows" :key="row.id">
              <td class="text-left">
                <BsCheckbox
                  :id="checkId(`division-${row.id}`)"
                  v-bind="stateOf(row.keys)"
                  :label="row.name"
                  @change="(value) => emit('change', row.keys, value)"
                ></BsCheckbox>
              </td>
              <td v-for="cell in row.cells" :key="cell.key">
                <BsCheckbox
                  :id="checkId(cell.key)"
                  :checked="is_visible(cell.key)"
                  :label="`${row.name} - ${cell.label}`"
                  hide-label
                  class="matrix-check"
                  @change="(value) => emit('change', [cell.key], value)"
                ></BsCheckbox>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="selector-section">
        <div class="checks-grid">
          <BsCheckbox
            v-for="column in noteColumns"
            :id="checkId(column.key)"
            :key="column.key"
            :checked="is_visible(column.key)"
            :label="column.label"
            @change="(value) => emit('change', [column.key], value)"
          ></BsCheckbox>
        </div>
      </div>
      <div class="text-right">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="hidden_count == 0"
          @click="emit('show-all')"
        >
          {{ t("showAll") }}
        </button>
      </div>
    </div>
  </BsDropdown>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Selector de columnas visibles de la Carga Académica. Las columnas por División de tiempo
// (horas, cupo, tipo de sala y periodicidad) se muestran como matriz, con checkboxes por
// fila (toda la división) y por columna (todo el apartado). El estado vive en
// useAcademicLoadColumns; este componente solo lo muestra y emite los cambios.
import { computed, getCurrentInstance } from "vue";
import BsDropdown from "@/components/reusable/bs/BsDropdown";
import BsCheckbox from "@/components/reusable/bs/BsCheckbox";
import {
  ALLOCATION_GROUPS,
  allocationColumnKey,
} from "./useAcademicLoadColumns";

const props = defineProps({
  time_allocations: {
    type: Array,
    required: true,
  },
  is_visible: {
    type: Function,
    required: true,
  },
  hidden_count: {
    type: Number,
    default: 0,
  },
});
// change(keys, visible)
const emit = defineEmits(["change", "show-all"]);

const proxy = getCurrentInstance().proxy;
const t = (key, values) =>
  proxy.$t(`studyPlan.academicLoad.columnsSelector.${key}`, values);
const label = (key) => proxy.$t(`studyPlan.academicLoad.${key}`);

const buttonText = computed(() =>
  props.hidden_count > 0
    ? t("hiddenCount", { count: props.hidden_count })
    : proxy.$t("reusable.common.displayColumns")
);

const subjectColumns = computed(() => [
  { key: "period", label: label("period") },
  { key: "internal_code", label: label("internalCode") },
  { key: "code", label: label("code") },
  { key: "name", label: label("name"), disabled: true },
  { key: "prerequisites", label: label("prerequisites") },
  { key: "pucv_credits", label: label("pucvCredits") },
  { key: "sct_credits", label: label("sctCredits") },
]);
const totalColumns = computed(() => [
  { key: "presential", label: t("presential") },
  { key: "autonomous", label: t("autonomous") },
  { key: "dedication", label: t("dedication") },
]);
const noteColumns = computed(() => [
  { key: "quota_justification", label: label("quotaJustification") },
  { key: "academic_load_observation", label: t("observation") },
]);

const groupLabels = computed(() => ({
  hours: t("hours"),
  quota: t("quota"),
  classroom_type: t("classroomType"),
  periodicity: t("periodicity"),
}));
const groups = computed(() =>
  ALLOCATION_GROUPS.map((group) => ({
    key: group,
    label: groupLabels.value[group],
    keys: props.time_allocations.map((time_allocation) =>
      allocationColumnKey(group, time_allocation)
    ),
  }))
);
const allKeys = computed(() => groups.value.flatMap((group) => group.keys));
const allocationRows = computed(() =>
  props.time_allocations.map((time_allocation) => {
    const cells = ALLOCATION_GROUPS.map((group) => ({
      key: allocationColumnKey(group, time_allocation),
      label: groupLabels.value[group],
    }));
    return {
      id: time_allocation.id,
      name: time_allocation.name,
      cells: cells,
      keys: cells.map((cell) => cell.key),
    };
  })
);

function checkId(key) {
  return `academic-load-column-${key.replace(":", "-")}`;
}

// Checkbox que agrupa varias columnas: marcado si todas se ven, indeterminado si algunas
function stateOf(keys) {
  const visible_count = keys.filter((key) => props.is_visible(key)).length;
  return {
    checked: keys.length > 0 && visible_count == keys.length,
    indeterminate: visible_count > 0 && visible_count < keys.length,
  };
}
</script>

<style scoped>
.columns-selector {
  font-size: var(--secondary-font-size);
}
.selector-section {
  border-bottom: 1px solid var(--kl-border, #dee2e6);
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
}
.section-title {
  font-weight: var(--kl-fw-bold);
  margin-bottom: 0.25rem;
}
.checks-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 0.75rem;
}
.allocations-matrix {
  width: 100%;
}
.allocations-matrix th {
  font-weight: var(--kl-fw-bold);
  text-align: center;
  padding: 0 0.25rem 0.25rem;
  white-space: nowrap;
}
.allocations-matrix td {
  text-align: center;
  padding: 0.1rem 0.25rem;
}
.all-row td {
  border-bottom: 1px dashed var(--kl-border, #dee2e6);
}
/* Checkbox sin texto: el custom-control deja 1.5rem a la izquierda para la casilla */
.matrix-check {
  display: inline-block;
  padding-left: 1rem;
}
</style>

<style>
/* Panel del selector: ancho para la matriz y scroll si hay muchas Divisiones de tiempo */
.dropdown-menu.columns-menu {
  width: min(32rem, calc(100vw - 2rem));
  max-height: 70vh;
  overflow-y: auto;
  padding: 0.75rem;
  z-index: 1030;
}
</style>
